import { FC } from "react";
import { ConfigProvider, Popover } from "antd";
import { useFeatureFlag } from "Hooks/useFeatureFlag";
import { TypeUpdateStatus } from "Data/interfaces/Activities/IUpdateActivityRequest";
import { IDisciplineResponse } from "Data/interfaces/Obra/IDisciplineResponse";
import { IUserInforResponse } from "Data/interfaces/Auth/IUserInfoResponse";
import { IStage } from "Data/interfaces/Obra/IObraStages";
import {
  ITopicCommunicationType,
  ITopicDnDResponseOptionalGuid,
  ITopicPoint,
  ITopicStatus,
} from "Data/interfaces/Activities/IDragAndDropDataFormat";
import { ICustomOption } from "./useOptionsSelect";
import useShowCreateActivity from "./useShowCreateActivity";
import ConfirmCloseModal from "./Modals/ConfirmCloseModal";
import ConfirmDeleteModal from "./Modals/ConfirmDeleteModal";
import { Icon } from "Components/UI";
import Modal from "Components/UI/Modal";
import Button from "Components/UI/Button";
import Tooltip from "Components/UI/Tooltip";
import SpinCustom from "Components/UI/SpinCustom";
import CommentSignalR from "Components/UI/SignalR/comment";
import LoadingActivitiesExternalLinkHeader from "Components/UI/CustomLoading/LoadingActivitiesExternalLinkHeader";
import LoadingActivitiesExternalLinkContent from "Components/UI/CustomLoading/LoadingActivitiesExternalLinkContent";
import MoreActionDropdown from "./components/MoreActionsDropdown";
import FormContent from "./components/FormContent";
import ActionsContent from "./components/ActionsContent";
import CreateStage from "./components/CreateStage";
import ReportDropdown from "./components/ReportDropdown";
import ConfirmActionsDropdown from "./components/ConfirmActionsDropdown";
import { TypeMoreActionsType } from "Pages/Activities/useActivity";
import AccessPermission from "Utils/AcessPermission";
import {
  ButtonsWrapper,
  Content,
  FooterHr,
  LeftContent,
  RightContent,
  TooltipLink,
  VerticalHr,
  Wrapper,
} from "./styles";

interface ICreateActivity {
  visible: boolean;
  loadingExternalLink: boolean;
  loading: boolean;
  loadingUpdate: boolean;
  loadingDelete: boolean;
  editStage: boolean;
  updateActivity: ITopicDnDResponseOptionalGuid | null;
  error?: string;
  csId: number;
  title: string;
  topicStatus: ITopicStatus[];
  topicStages: IStage[];
  topicPoints: ITopicPoint[];
  topicCommunicationType: ITopicCommunicationType[];
  visibilityOptions: ICustomOption[];
  priorityOptions: ICustomOption[];
  priorityNoBgOptions: ICustomOption[];
  statusOptions: ICustomOption[];
  typeOptions: ICustomOption[];
  stageOptions: string[];
  pointsOptions: string[];
  communicationOptions: string[];
  userOptions: ICustomOption[];
  disciplineOptions: ICustomOption[];
  disciplines?: IDisciplineResponse[];
  allowOnlyStagesLinkedToDisciplinesInTopics: boolean;
  createStageLoading: boolean;
  updateStatus: TypeUpdateStatus;
  getOld: boolean;
  newActivityGuid?: string;
  userInfo?: IUserInforResponse;
  moreActionsType?: TypeMoreActionsType;
  isActivityPage?: boolean;
  onOpenShowActivity: (
    activitieId: string,
    moreActionsType?: TypeMoreActionsType
  ) => void;
  onCloseOpenCreateActivity?: () => void;
  onDuplicateActivity?: (activityId: string) => void;
  onSetMoreActionsType?: (value: TypeMoreActionsType) => void;
  onOpenViewpoint?: (topicId: string, viewpointId: string) => void;
  onCancel: () => void;
}

export type TypeCustomError = 'title' |
  'discipline' |
  'point' |
  'date' |
  'stage';

const ShowCreateActivity: FC<ICreateActivity> = ({
  visible,
  loading,
  loadingExternalLink,
  loadingUpdate,
  loadingDelete,
  editStage,
  title,
  csId,
  topicStatus,
  topicStages,
  topicPoints,
  topicCommunicationType,
  updateActivity,
  visibilityOptions,
  priorityOptions,
  priorityNoBgOptions,
  statusOptions,
  typeOptions,
  stageOptions,
  pointsOptions,
  communicationOptions,
  userOptions,
  disciplineOptions,
  disciplines,
  updateStatus,
  getOld,
  allowOnlyStagesLinkedToDisciplinesInTopics,
  createStageLoading,
  newActivityGuid,
  userInfo,
  moreActionsType,
  isActivityPage,
  onCancel,
  onCloseOpenCreateActivity,
  onOpenShowActivity,
  onDuplicateActivity,
  onSetMoreActionsType,
  onOpenViewpoint,
}) => {
  const followActivityFeatureFlag = useFeatureFlag('follow-activity-feature');
  const followActivityFeature = followActivityFeatureFlag.enabled && followActivityFeatureFlag.value === 'test';

  const {
    isSubmit,
    titleValue,
    defaultValues,
    showCancelModal,
    showDeleteModal,
    showToolTip,
    errors,
    showCreateStage,
    createStageName,
    selectedDisciplines,
    openMoreActions,
    handleOpenMoreActions,
    handleSetMoreActionsType,
    showCreateStageModal,
    closeCreateStageModal,
    handleCreateStage,
    handleTitleValue,
    copyInviteLink,
    submit,
    dynamicUpdateSubmit,
    confirmCancelShowCreateActivity,
    confirmDeleteActivity,
    openCancelModal,
    openDeleteModal,
    handleSelectedDisciplines,
    handleClearCustomError,
    renderEmpty,
    handleCustomError,
    closeCancelModal,
    closeDeleteModal,
  } = useShowCreateActivity({
    loading,
    loadingExternalLink,
    loadingUpdate,
    loadingDelete,
    editStage,
    updateActivity,
    csId,
    topicStatus,
    topicStages,
    allowOnlyStagesLinkedToDisciplinesInTopics,
    topicPoints,
    topicCommunicationType,
    typeOptions,
    updateStatus,
    getOld,
    newActivityGuid,
    userInfo,
    createStageLoading,
    moreActionsType,
    onOpenShowActivity,
    onCloseOpenCreateActivity,
    onSetMoreActionsType,
    onCancel,
  });

  const isUpdate = !!updateActivity?.Guid;
  const updateLoading = updateStatus === 'atualizando';
  const updateSave = updateStatus === 'atualizado';
  const deletePermission = AccessPermission.deleteActivitiePermission(
    userInfo,
    updateActivity?.CreationAuthor,
    updateActivity?.TopicAssignedUsers,
    updateActivity?.Visibility
  );

  const footerButtons = (
    <ButtonsWrapper isactivitypage={isActivityPage}>
      <Button
        onClick={openCancelModal}
        type="text"
        className="buttonCancel"
      >
        Cancelar
      </Button>
      <div className="buttonConfirmWrapper">
        <Button
          type="primary"
          htmlType="submit"
          onClick={() => handleSetMoreActionsType('createAndOpen')}
          className="buttonSubmit"
          disabled={loading}
          loading={loading}
        >
          Cadastrar
        </Button>
        {isActivityPage && (
          <>
            <span className="separator" />
            <Popover
              content={
                <ConfirmActionsDropdown
                  onClose={() => handleOpenMoreActions(false)}
                  onCreateAndOpen={() => handleSetMoreActionsType('createAndOpen')}
                  onCreateAndNew={() => handleSetMoreActionsType('createAndNew')}
                  onCreateAndCopy={() => handleSetMoreActionsType('createAndCopy')}
                />
              }
              placement="bottomRight"
              trigger="click"
              open={openMoreActions}
              onOpenChange={handleOpenMoreActions}
              overlayInnerStyle={{ padding: 0 }}
            >
              <Button type="primary" className="buttonConfirmActions">
                <Icon
                  icon="expandir"
                  customSize={10}
                  className="iconConfirmActions"
                />
              </Button>
            </Popover>
          </>
        )}
      </div>
    </ButtonsWrapper>
  );

  return (
    <Modal
      visible={visible}
      onCancel={isUpdate ? confirmCancelShowCreateActivity : openCancelModal}
      footerButtons={(isUpdate || loadingExternalLink) ? null : footerButtons}
      footerActive={false}
      width={874}
      zIndex={showCancelModal ? 9 : undefined}
      customClassName
    >
      <ConfigProvider renderEmpty={() => renderEmpty()}>
        {updateActivity?.Guid && (
          <CommentSignalR topicId={updateActivity.Guid} userInfo={userInfo} />
        )}
        <Wrapper>
          {loadingExternalLink && (
            <LoadingActivitiesExternalLinkHeader />
          )}
          {!loadingExternalLink && (
            <div className="header">
              <span className="titleHeader">
                {isUpdate
                  ? (
                    <span>
                      <span className="titleHeaderBold">
                        #{updateActivity.Identifier}
                      </span>
                      {` / ${updateActivity.Type?.Name}`}
                    </span>
                  )
                  : title}
              </span>
              {isUpdate && (
                <div className="multipleIconHeader">
                  {updateStatus && (
                    <div className="updateStatus">
                      {updateLoading && (
                        <span className="updateStatusSpan">
                          <SpinCustom
                            size={14}
                          />
                          <span className="updateStatusTextLoading">
                            Salvando
                          </span>
                        </span>
                      )}
                      {updateSave && (
                        <span className="updateStatusSpan">
                          <Icon
                            icon="confirmacao"
                            color="verde"
                            customSize={14}
                          />
                          <span className="updateStatusTextSave">
                            Salvo
                          </span>
                        </span>
                      )}
                    </div>
                  )}
                  {isUpdate && updateActivity.ConstructionSiteDisciplines.length <= 0 &&
                    <Tooltip
                      placement="top"
                      overlay="Todos os campos obrigatórios precisam estar preenchidos."
                    >
                      <span>
                        <Icon
                          icon="aviso"
                          customSize={14}
                          customColor={'#FF3D57'}
                          className="iconHeaderAlarm"
                        />
                      </span>
                    </Tooltip>
                  }
                  {followActivityFeature && isUpdate && (
                    <ReportDropdown
                      topicId={updateActivity.Guid as string}
                      assignedUsers={updateActivity.TopicAssignedUsers}
                    />
                  )}
                  <div className="iconHeader">
                    <Tooltip
                      overlay={
                        showToolTip && (
                          <TooltipLink>
                            <Icon icon="confirmacao" customColor="#09B66D" />
                            <span>Link copiado para a área de transferência</span>
                          </TooltipLink>
                        )
                      }
                      placement="top"
                      open={showToolTip}
                    >
                      <Icon
                        icon="link"
                        customSize={12}
                        customColor={'#324150'}
                        onClick={() => copyInviteLink(updateActivity.Guid as string)}
                      />
                    </Tooltip>
                  </div>
                  <MoreActionDropdown
                    hasDeletePermission={deletePermission}
                    isActivityPage={isActivityPage}
                    onDelete={() => openDeleteModal()}
                    onCopyLink={() => copyInviteLink(updateActivity.Guid as string)}
                    onDuplicate={() => onDuplicateActivity && onDuplicateActivity(updateActivity.Guid as string)}
                  />
                </div>
              )}
              {!isUpdate && (
                <span className="iconHeader">
                  <Icon
                    icon="cancelar"
                    customSize={8}
                    customColor={'#324150'}
                    onClick={isUpdate ? confirmCancelShowCreateActivity : openCancelModal}
                  />
                </span>
              )}
            </div>
          )}

          <Content>
            <LeftContent>
              {loadingExternalLink && (
                <LoadingActivitiesExternalLinkContent />
              )}
              {!loadingExternalLink && (
                <FormContent
                  updateActivity={updateActivity}
                  visibilityOptions={visibilityOptions}
                  priorityOptions={priorityOptions}
                  priorityNoBgOptions={priorityNoBgOptions}
                  statusOptions={statusOptions}
                  typeOptions={typeOptions}
                  stageOptions={stageOptions}
                  pointsOptions={pointsOptions}
                  communicationOptions={communicationOptions}
                  userOptions={userOptions}
                  disciplineOptions={disciplineOptions}
                  stages={topicStages}
                  allowOnlyStagesLinkedToDisciplinesInTopics={allowOnlyStagesLinkedToDisciplinesInTopics}
                  userInfo={userInfo}
                  isSubmit={isSubmit}
                  titleValue={titleValue}
                  defaultValues={defaultValues}
                  errors={errors}
                  selectedDisciplines={selectedDisciplines}
                  handleTitleValue={handleTitleValue}
                  submit={submit}
                  dynamicUpdateSubmit={dynamicUpdateSubmit}
                  handleClearCustomError={handleClearCustomError}
                  handleCustomError={handleCustomError}
                  onShowCreateStageModal={showCreateStageModal}
                  onSelectedDisciplines={handleSelectedDisciplines}
                />
              )}
            </LeftContent>

            <VerticalHr />
            <RightContent>
              <ActionsContent
                updateActivity={updateActivity}
                csId={csId}
                loadingExternalLink={loadingExternalLink}
                tempMoreActionsType={moreActionsType}
                onOpenViewpoint={onOpenViewpoint}
              />
            </RightContent>
          </Content>
        </Wrapper>

        <FooterHr />
      </ConfigProvider>

      {showCancelModal && (
        <ConfirmCloseModal
          visible={showCancelModal}
          onCancel={() => closeCancelModal()}
          onConfirm={confirmCancelShowCreateActivity}
        />
      )}
      {(showDeleteModal && updateActivity) && (
        <ConfirmDeleteModal
          visible={showDeleteModal}
          onCancel={() => closeDeleteModal()}
          loading={loadingDelete}
          onConfirm={() => confirmDeleteActivity()}
          activitieIdentifier={updateActivity.Identifier}
        />
      )}
      {showCreateStage && (
        <CreateStage
          visible={showCreateStage}
          editName={createStageName}
          disciplines={disciplines}
          disciplinesSelectedsFk={defaultValues?.constructionSiteDisciplinesFk ||
            selectedDisciplines
          }
          allowOnlyStagesLinkedToDisciplinesInTopics={allowOnlyStagesLinkedToDisciplinesInTopics}
          loading={createStageLoading}
          stages={topicStages}
          onSubmit={handleCreateStage}
          onCancel={closeCreateStageModal}
        />
      )}
    </Modal>
  );
}

export default ShowCreateActivity;
